import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import MaterialTable from 'material-table';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import moment from 'moment/min/moment-with-locales';
import { FirebaseContext, api } from 'common';
import { onValue } from 'firebase/database';
import CircularLoading from '../components/CircularLoading';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import theme from 'styles/tableStyle';
import { colors } from '../components/Theme/WebTheme';
import { SECONDORY_COLOR } from '../common/sharedFunctions';
import { textOrDash } from '../common/adminDisplay';
import { courierModeLookup } from '../common/eatCatalog';
import { formatAdminMoney, getEatCourierSettlement, getSettlementStyle } from '../common/adminFinance';

const imagePreview = (url, label) => url ? (
  <img
    src={url}
    alt={label}
    style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover', display: 'block' }}
  />
) : '-';

const documentLink = (url) => url ? <a href={url} target="_blank" rel="noreferrer">Voir</a> : '-';
const hasBankDetails = (rowData = {}) => Boolean(rowData.bankName && rowData.bankCode && rowData.bankAccount);
const bankDetailsStatus = (rowData = {}) => hasBankDetails(rowData) ? 'Renseigné' : '-';

const buildRatingSummaries = (ratingsRoot = {}) => {
  return Object.keys(ratingsRoot || {}).reduce((summaries, id) => {
    const reviews = Object.values(ratingsRoot[id] || {})
      .map(review => ({
        ...review,
        rate: Number(review.rate || review.rating || 0),
        createdAt: Number(review.createdAt || review.ratedAt || 0)
      }))
      .filter(review => review.rate > 0);
    const ratingCount = reviews.length;
    const rating = ratingCount ? reviews.reduce((sum, review) => sum + review.rate, 0) / ratingCount : 0;
    const latestReview = reviews.sort((left, right) => right.createdAt - left.createdAt)[0] || null;
    summaries[id] = {
      rating: rating ? Number(rating.toFixed(1)) : null,
      ratingCount,
      latestFeedback: latestReview && latestReview.feedback ? latestReview.feedback : '',
      latestRatedAt: latestReview ? latestReview.createdAt : null
    };
    return summaries;
  }, {});
};

const applicationStatusLookup = {
  documents_required: 'Documents requis',
  pending_review: 'À valider',
  approved: 'Validé',
  rejected: 'Refusé'
};

const thermalBagStatusLookup = {
  to_deliver: 'À livrer',
  prepared: 'Préparé',
  delivered: 'Livré'
};

export default function EatCouriers() {
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const loaded = useRef(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const couriers = useSelector(state => state.eatdata.couriers || []);
  const orders = useSelector(state => state.eatdata.orders || []);
  const loading = useSelector(state => state.eatdata.loading);
  const settings = useSelector(state => state.settingsdata.settings || {});
  const [selectedRow, setSelectedRow] = useState(null);
  const [ratingSummaries, setRatingSummaries] = useState({});

  useEffect(() => {
    dispatch(api.fetchEatCouriers());
    dispatch(api.fetchEatOrders());
    loaded.current = true;
  }, [dispatch]);

  useEffect(() => {
    if (!firebase || !firebase.eatRatingsRef) return undefined;
    return onValue(firebase.eatRatingsRef, snapshot => {
      const ratingsRoot = snapshot.val() || {};
      setRatingSummaries(buildRatingSummaries(ratingsRoot.couriers || {}));
    });
  }, [firebase]);

  const sortedData = useMemo(() => {
    return [...couriers]
      .filter(courier => !courier.archived && !courier.deletedAt)
      .map(courier => {
        const liveRating = ratingSummaries[courier.uid || courier.id] || {};
        return {
          ...courier,
          ...(() => {
            const settlement = getEatCourierSettlement(orders, courier.uid || courier.id);
            return {
              settlementBalance: settlement.platformOwes,
              platformOwesAmount: settlement.platformOwes,
              accountingDeliveryCount: settlement.count,
              accountingLastActivity: settlement.lastActivity
            };
          })(),
          rating: liveRating.rating,
          ratingCount: liveRating.ratingCount || 0,
          latestFeedback: liveRating.latestFeedback || '',
          latestRatedAt: liveRating.latestRatedAt || null
        };
      })
      .sort((left, right) => (right.updatedAt || right.createdAt || 0) - (left.updatedAt || left.createdAt || 0));
  }, [couriers, orders, ratingSummaries]);

  const updateCourier = (rowData, patch) => {
    dispatch(api.updateEatCourier(rowData.id || rowData.uid, patch));
  };

  const createCourier = (rowData) => {
    const courierId = textOrDash(rowData.uid, '') || textOrDash(rowData.id, '') || `eat-courier-${Date.now()}`;
    dispatch(api.updateEatCourier(courierId, {
      uid: courierId,
      firstName: rowData.firstName || '',
      lastName: rowData.lastName || '',
      mobile: rowData.mobile || '',
      email: rowData.email || '',
      transportMode: rowData.transportMode || 'bike',
      profileImageUrl: rowData.profileImageUrl || '',
      siret: rowData.siret || '',
      nafCode: rowData.nafCode || '',
      idImageUrl: rowData.idImageUrl || '',
      idImageBackUrl: rowData.idImageBackUrl || '',
      rcProUrl: rowData.rcProUrl || '',
      bankName: rowData.bankName || '',
      bankCode: rowData.bankCode || '',
      bankAccount: rowData.bankAccount || '',
      applicationStatus: rowData.applicationStatus || 'documents_required',
      thermalBagStatus: rowData.thermalBagStatus || 'to_deliver',
      city: rowData.city || '',
      approved: Boolean(rowData.approved),
      active: Boolean(rowData.active),
      createdAt: Date.now()
    }));
  };

  const columns = [
    {
      title: 'Créé le',
      field: 'createdAt',
      editable: 'never',
      render: rowData => rowData.createdAt ? moment(rowData.createdAt).format('lll') : '-'
    },
    { title: 'UID livreur', field: 'uid', render: rowData => textOrDash(rowData.uid || rowData.id) },
    { title: 'Prénom', field: 'firstName', render: rowData => textOrDash(rowData.firstName) },
    { title: 'Nom de famille', field: 'lastName', render: rowData => textOrDash(rowData.lastName) },
    { title: 'Téléphone', field: 'mobile', render: rowData => textOrDash(rowData.mobile) },
    { title: 'E-mail', field: 'email', render: rowData => textOrDash(rowData.email) },
    { title: 'Photo', field: 'profileImageUrl', render: rowData => imagePreview(rowData.profileImageUrl, rowData.firstName || 'Livreur') },
    { title: 'Mode', field: 'transportMode', lookup: courierModeLookup, render: rowData => textOrDash(courierModeLookup[rowData.transportMode] || rowData.transportMode) },
    { title: 'Statut dossier', field: 'applicationStatus', lookup: applicationStatusLookup, render: rowData => textOrDash(applicationStatusLookup[rowData.applicationStatus] || rowData.applicationStatus) },
    { title: 'SIRET', field: 'siret', render: rowData => textOrDash(rowData.siret) },
    { title: 'Code NAF', field: 'nafCode', render: rowData => textOrDash(rowData.nafCode) },
    { title: 'Identité recto', field: 'idImageUrl', render: rowData => documentLink(rowData.idImageUrl) },
    { title: 'Identité verso', field: 'idImageBackUrl', render: rowData => documentLink(rowData.idImageBackUrl) },
    { title: 'RC Pro', field: 'rcProUrl', render: rowData => documentLink(rowData.rcProUrl) },
    { title: 'RIB / compte', field: 'bankAccount', render: rowData => textOrDash(rowData.bankAccount) },
    { title: 'Banque', field: 'bankName', render: rowData => textOrDash(rowData.bankName) },
    { title: 'Code banque', field: 'bankCode', render: rowData => textOrDash(rowData.bankCode) },
    { title: 'Coordonnées bancaires', field: 'bankDetailsStatus', editable: 'never', render: rowData => bankDetailsStatus(rowData) },
    { title: 'Sac isotherme', field: 'thermalBagStatus', lookup: thermalBagStatusLookup, render: rowData => textOrDash(thermalBagStatusLookup[rowData.thermalBagStatus] || rowData.thermalBagStatus) },
    {
      title: 'Règlement',
      field: 'settlementBalance',
      type: 'numeric',
      defaultSort: 'desc',
      editable: 'never',
      render: rowData => {
        const style = getSettlementStyle(rowData.settlementBalance);
        return <Chip size="small" label={`À payer ${formatAdminMoney(rowData.platformOwesAmount, settings)}`} sx={{ color: style.color, backgroundColor: style.backgroundColor, border: `1px solid ${style.borderColor}`, fontWeight: 900 }} />;
      }
    },
    { title: 'À payer livreur', field: 'platformOwesAmount', type: 'numeric', editable: 'never', render: rowData => formatAdminMoney(rowData.platformOwesAmount, settings) },
    { title: 'Livraisons compta', field: 'accountingDeliveryCount', type: 'numeric', editable: 'never' },
    { title: 'Dernière activité compta', field: 'accountingLastActivity', editable: 'never', render: rowData => rowData.accountingLastActivity ? moment(rowData.accountingLastActivity).format('lll') : '-' },
    { title: 'Note app', field: 'rating', editable: 'never', type: 'numeric', render: rowData => rowData.rating ? `${Number(rowData.rating).toFixed(1)} / 5` : '-' },
    { title: 'Avis app', field: 'ratingCount', editable: 'never', type: 'numeric', render: rowData => rowData.ratingCount ? rowData.ratingCount : '-' },
    { title: 'Dernier avis', field: 'latestFeedback', editable: 'never', render: rowData => textOrDash(rowData.latestFeedback) },
    { title: 'Dernière note', field: 'latestRatedAt', editable: 'never', render: rowData => rowData.latestRatedAt ? moment(rowData.latestRatedAt).format('lll') : '-' },
    { title: 'Ville', field: 'city', render: rowData => textOrDash(rowData.city) },
    {
      title: 'Approuvé',
      field: 'approved',
      type: 'boolean',
      render: rowData => (
        <Switch
          disabled={!settings.AllowCriticalEditsAdmin}
          checked={Boolean(rowData.approved)}
          onChange={() => updateCourier(rowData, { approved: !rowData.approved, applicationStatus: !rowData.approved ? 'approved' : 'pending_review' })}
        />
      )
    },
    {
      title: 'En ligne',
      field: 'active',
      type: 'boolean',
      render: rowData => (
        <Switch
          disabled={!settings.AllowCriticalEditsAdmin}
          checked={Boolean(rowData.active)}
          onChange={() => updateCourier(rowData, { active: !rowData.active })}
        />
      )
    },
    { title: 'Commande en cours', field: 'currentOrderId', editable: 'never', render: rowData => textOrDash(rowData.currentOrderId) },
    { title: 'Mis à jour', field: 'updatedAt', editable: 'never', render: rowData => rowData.updatedAt ? moment(rowData.updatedAt).format('lll') : '-' }
  ];

  if (!loaded.current && loading) {
    return <CircularLoading />;
  }

  return (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title="Livreurs Allô Driver Eats"
        columns={columns}
        data={sortedData}
        onRowClick={(event, rowData) => rowData && setSelectedRow(rowData.tableData.id)}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: '8px',
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: '20px'
        }}
        options={{
          exportButton: { csv: true, pdf: false },
          maxColumnSort: 'all_columns',
          rowStyle: rowData => ({
            backgroundColor: selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
          }),
          ...TableStyle()
        }}
        localization={localization(t)}
        editable={{
          onRowAdd: newData => new Promise(resolve => {
            setTimeout(() => {
              resolve();
              createCourier(newData);
            }, 400);
          }),
          onRowUpdate: (newData, oldData) => new Promise(resolve => {
            setTimeout(() => {
              resolve();
              updateCourier(oldData, {
                firstName: newData.firstName || '',
                lastName: newData.lastName || '',
                mobile: newData.mobile || '',
                email: newData.email || '',
                transportMode: newData.transportMode || 'bike',
                profileImageUrl: newData.profileImageUrl || '',
                siret: newData.siret || '',
                nafCode: newData.nafCode || '',
                idImageUrl: newData.idImageUrl || '',
                idImageBackUrl: newData.idImageBackUrl || '',
                rcProUrl: newData.rcProUrl || '',
                bankName: newData.bankName || '',
                bankCode: newData.bankCode || '',
                bankAccount: newData.bankAccount || '',
                applicationStatus: newData.applicationStatus || 'documents_required',
                thermalBagStatus: newData.thermalBagStatus || 'to_deliver',
                city: newData.city || '',
                approved: Boolean(newData.approved),
                active: Boolean(newData.active)
              });
            }, 400);
          }),
          onRowDelete: oldData => new Promise(resolve => {
            setTimeout(() => {
              resolve();
              updateCourier(oldData, { archived: true, deletedAt: Date.now(), active: false });
            }, 400);
          })
        }}
      />
    </ThemeProvider>
  );
}
